import { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  Card, Table, Button, Space, Tag, Tooltip, Popconfirm,
  DatePicker, Typography, Alert, Segmented, Progress,
} from 'antd';
import {
  DeleteOutlined, CheckOutlined, WarningOutlined, ArrowLeftOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

import { APIClient } from '../../helpers/api_helper';
import IntentTypeSelect from '../../Common/IntentTypeSelect';
import CategorySelect from '../../Common/CategorySelect';
import CompanySelect from '../../Common/CompanySelect';
import AccountSelect from '../../Common/AccountSelect';
import TagsSelect from '../../Common/TagsSelect';
import DebouncedInput from '../../Common/DebouncedInput';
import DebouncedNumberInput from '../../Common/DebouncedNumberInput';
import MoneyDisplay from '../../Common/MoneyDisplay';
import { intentFieldLabel } from '../../Common/intentFieldLabels';
import { useOrgConfig } from '../../Common/Hooks/OrgConfigHooks';
import { triggerErrorNotif } from '../../slices/notifications/thunk';
import { ATTEMPT_STATUS_META, ROW_STATUS_META } from './constants';

const api = new APIClient();

// Rows the user still has to do something about: promotion already failed, or
// the row is missing something and cannot be promoted yet. Two different fields
// on the server, one thing to the person reviewing the statement.
const hasProblem = (row) =>
  row.status === 'ERROR' || !!row.error || !!row.validation_error;

const ALL = 'ALL';
const ISSUES = 'ISSUES';

// The server pages staged rows, this table does not. Filtering, the counts on
// the filter buttons and "select all" all have to mean the whole statement, so
// a year's worth of transactions -- comfortably past one page -- is pulled in
// full before anything is shown. 1000 is the server's max_page_size; the page
// cap is only there so a bad `count` cannot spin forever.
const ROWS_PAGE_SIZE = 1000;
const MAX_ROW_PAGES = 25;

// Rows promoted per request. Small enough that the bar moves often and no
// single request runs long enough to look hung; large enough that a ten
// thousand row statement is a hundred requests, not ten thousand.
const PROMOTE_CHUNK = 100;

const fetchAllRows = async (batchId, extra = {}) => {
  const all = [];
  for (let page = 1; page <= MAX_ROW_PAGES; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const resp = await api.get('/api/staged_transactions/', {
      batch: batchId, page, page_size: ROWS_PAGE_SIZE, ...extra,
    });
    const list = resp?.results || [];
    all.push(...list);
    if (!list.length || all.length >= (resp?.pagination?.count ?? all.length)) break;
  }
  return all;
};

/**
 * One import attempt, line by line.
 *
 * Nothing here is committed until "Shrani": the user is expected to leave rows
 * sitting while they work out what a payment was, and come back later.
 */
const AttemptReview = ({ attemptId, onBack }) => {
  const dispatch = useDispatch();
  const orgConfig = useOrgConfig();
  // Column headings come from the drawer's vocabulary, never invented here.
  const label = (field) => intentFieldLabel(field, orgConfig);

  const [attempt, setAttempt] = useState(null);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState(ALL);
  // {done, total} while a save is running, null otherwise.
  const [progress, setProgress] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  const loadAttempt = useCallback(
    () => api.get(`/api/staging_batches/${attemptId}/`).then((resp) => {
      if (resp) setAttempt(resp);
      return resp;
    }),
    [attemptId]
  );

  const loadRows = useCallback(() => {
    setLoading(true);
    return fetchAllRows(attemptId)
      .then((list) => {
        setRows(list);
        // Duplicates are opt-in: a user who wanted them would have to say so.
        setSelected(list.filter(r => r.status === 'PENDING').map(r => r.id));
        return list;
      })
      .finally(() => setLoading(false));
  }, [attemptId]);

  useEffect(() => { loadAttempt(); loadRows(); }, [loadAttempt, loadRows]);

  const reload = () => Promise.all([loadAttempt(), loadRows()]);

  // --- filtering -----------------------------------------------------------
  // Counted off the loaded rows rather than attempt.counts, so the numbers on
  // the buttons always describe what the table will actually show -- and so the
  // cross-cutting "Z napako" bucket, which is not a status, can be counted at
  // all.
  const matchesFilter = (row) => {
    if (statusFilter === ALL) return true;
    if (statusFilter === ISSUES) return hasProblem(row);
    return row.status === statusFilter;
  };

  const issueCount = rows.filter(hasProblem).length;
  const statusCounts = rows.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});

  const filterOptions = [
    { value: ALL, label: `Vse (${rows.length})` },
    // Only offered when it covers something the status buttons below do not --
    // a row that is still "Za pregled" but cannot be promoted as it stands.
    // When every problem row already failed, this would be "Napaka" twice.
    ...(issueCount > (statusCounts.ERROR || 0)
      ? [{ value: ISSUES, label: <span><WarningOutlined /> Z napako ({issueCount})</span> }]
      : []),
    // Statuses in the order the constants declare them, skipping any the batch
    // does not contain.
    ...Object.entries(ROW_STATUS_META)
      .filter(([status]) => statusCounts[status])
      .map(([status, meta]) => ({
        value: status,
        label: `${meta.label} (${statusCounts[status]})`,
      })),
  ];

  const visibleRows = rows.filter(matchesFilter);
  const visibleIds = new Set(visibleRows.map(r => r.id));

  // Every action below works on this, never on `selected` -- a row hidden by the
  // filter must never be saved or deleted by a button the user pressed while
  // looking at something else. Clearing the filter brings the rest back.
  const effectiveSelected = selected.filter(id => visibleIds.has(id));


  const patchRow = (id, fields) => {
    // Optimistic: the table must not flicker on every keystroke of a debounced
    // field. The server response then overwrites with the canonical row, which
    // is what refreshes validation_error and status.
    setRows(prev => prev.map(r => (r.id === id ? { ...r, ...fields } : r)));
    return api.update(`/api/staged_transactions/${id}/`, fields, { successMsg: null })
      .then((resp) => {
        if (resp) setRows(prev => prev.map(r => (r.id === id ? resp : r)));
      });
  };

  const bulkSet = (fields) => {
    setSaving(true);
    api.post('/api/staged_transactions/bulk_update/',
      effectiveSelected.length
        ? { ids: effectiveSelected, fields }
        : { batch: attemptId, fields },
      { successMsg: 'Vrstice posodobljene' })
      .then(reload)
      .finally(() => setSaving(false));
  };

  // Same endpoint as the bulk delete: one id is just a short list. Kept
  // separate so the row button does not depend on the row being selected.
  const deleteRow = (id) => {
    setSaving(true);
    api.post('/api/staged_transactions/delete_bulk/',
      { ids: [id] },
      { successMsg: 'Vrstica izbrisana' })
      .then(reload)
      .finally(() => setSaving(false));
  };

  const deleteSelected = () => {
    setSaving(true);
    api.post('/api/staged_transactions/delete_bulk/',
      { ids: effectiveSelected },
      { successMsg: 'Vrstice izbrisane' })
      .then(reload)
      .finally(() => setSaving(false));
  };

  const save = async ({ force = false, whole = false } = {}) => {
    if (!whole && !effectiveSelected.length) return;

    setSaving(true);
    try {
      // "Shrani vse" used to let one request resolve "every pending row"
      // server side. Saving in chunks means the ids have to be known up front,
      // so they are read back from the server rather than off the copy in
      // memory -- still one authoritative answer, just one request earlier.
      const ids = whole
        ? (await fetchAllRows(attemptId, { status: 'PENDING' })).map(r => r.id)
        : effectiveSelected;

      if (!ids.length) return;

      const totals = { promoted: 0, failed: 0, skipped_duplicate: 0 };
      setProgress({ done: 0, total: ids.length });

      for (let from = 0; from < ids.length; from += PROMOTE_CHUNK) {
        const chunk = ids.slice(from, from + PROMOTE_CHUNK);
        // eslint-disable-next-line no-await-in-loop
        const resp = await api.post(`/api/staging_batches/${attemptId}/promote/`,
          // Every chunk defers the tax rebuild; it runs once below. Without
          // this a tax-enabled org rebuilds 2024-2027 a hundred times over.
          { row_ids: chunk, force, defer_tax: true },
          { successMsg: null });

        const r = resp?.result || {};
        totals.promoted += r.promoted || 0;
        totals.failed += r.failed || 0;
        totals.skipped_duplicate += r.skipped_duplicate || 0;
        setProgress({ done: Math.min(from + PROMOTE_CHUNK, ids.length), total: ids.length });
      }

      if (totals.promoted) {
        // Deliberately outside the loop and unconditional on the last chunk's
        // own result: that chunk can fail wholesale while earlier ones saved.
        await api.post(`/api/staging_batches/${attemptId}/recompute_tax/`, {},
          { successMsg: null });
      }

      if (totals.failed) {
        dispatch(triggerErrorNotif(
          `Shranjenih ${totals.promoted}, neuspešnih ${totals.failed}|Napake so prikazane v vrsticah.`
        ));
      }
    } finally {
      // Whatever happened -- including a chunk that threw part way through --
      // the table has to show what actually got saved.
      setProgress(null);
      await reload();
      setSaving(false);
    }
  };

  const columns = [
    {
      title: '', dataIndex: 'id', width: 40, fixed: 'left',
      render: (id, row) => (
        // A promoted row is a saved transaction now; deleting its staging copy
        // would suggest the transaction went with it.
        row.status === 'PROMOTED' ? null : (
          <Popconfirm
            title="Izbriši to vrstico?"
            description="Vrstica bo odstranjena iz poskusa uvoza."
            okText="Izbriši" cancelText="Prekliči" okButtonProps={{ danger: true }}
            onConfirm={() => deleteRow(id)}
          >
            <Tooltip title="Izbriši vrstico">
              <Button size="small" type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        )
      ),
    },
    {
      title: 'Status', dataIndex: 'status', width: 120, fixed: 'left',
      render: (status, row) => {
        const meta = ROW_STATUS_META[status] || { color: 'default', label: status };
        const tag = <Tag color={meta.color}>{meta.label}</Tag>;
        if (row.error) {
          return <Tooltip title={row.error}><span>{tag}<WarningOutlined /></span></Tooltip>;
        }
        if (status === 'DUPLICATE' && row.duplicate_of_title) {
          return <Tooltip title={`Že obstaja: ${row.duplicate_of_title}`}>{tag}</Tooltip>;
        }
        if (row.validation_error) {
          return <Tooltip title={row.validation_error}><span>{tag}<WarningOutlined /></span></Tooltip>;
        }
        return tag;
      },
    },
    {
      title: label('date_to_be_paid'), dataIndex: 'date_to_be_paid', width: 140,
      render: (value, row) => (
        <DatePicker
          size="small" allowClear={false} format="DD.MM.YYYY"
          value={value ? dayjs(value) : null}
          disabled={row.status === 'PROMOTED'}
          onChange={(d) => patchRow(row.id, { date_to_be_paid: d ? d.format('YYYY-MM-DD') : null })}
        />
      ),
    },
    {
      title: label('type'), dataIndex: 'type', width: 230,
      render: (value, row) => (
        <IntentTypeSelect
          small value={value} hidden={['TAX']}
          disabled={row.status === 'PROMOTED'}
          onChange={(v) => patchRow(row.id, { type: v })}
        />
      ),
    },
    {
      title: label('amount'), dataIndex: 'amount', width: 140,
      render: (value, row) => (
        row.status === 'PROMOTED'
          ? <MoneyDisplay type={row.type} val={Number(value)} />
          : (
            <DebouncedNumberInput
              size="small" value={value} min={-Infinity}
              onChange={(v) => patchRow(row.id, { amount: v })}
            />
          )
      ),
    },
    {
      title: label('title'), dataIndex: 'title', width: 240,
      render: (value, row) => (
        <DebouncedInput
          size="small" value={value}
          disabled={row.status === 'PROMOTED'}
          onChange={(v) => patchRow(row.id, { title: v })}
        />
      ),
    },
    {
      title: label('company'), dataIndex: 'company', width: 220,
      render: (value, row) => (
        <CompanySelect
          size="small" minWidth={180} value={value || undefined}
          onChange={(v) => patchRow(row.id, { company: v || null })}
        />
      ),
    },
    {
      title: label('category'), dataIndex: 'category', width: 220,
      render: (value, row) => (
        <CategorySelect
          size="small" minWidth={180} value={value || undefined}
          onChange={(v) => patchRow(row.id, { category: v || null })}
        />
      ),
    },
    {
      title: label('tag'), dataIndex: 'tag', width: 200,
      render: (value, row) => (
        <TagsSelect
          size="small" minWidth={160} value={value || undefined}
          onChange={(v) => patchRow(row.id, { tag: v || null })}
        />
      ),
    },
    {
      // Only meaningful for a transfer, and showing it everywhere would imply
      // every row needs one.
      title: label('transfer_to_account'), dataIndex: 'transfer_to_account', width: 200,
      render: (value, row) => (
        row.type !== 'TRANSFER' ? null : (
          <AccountSelect
            size="small" minWidth={160} value={value || undefined}
            onChange={(v) => patchRow(row.id, {
              transfer_to_account: v || null,
              transfer_from_account: row.transfer_from_account || row.account,
            })}
          />
        )
      ),
    },
  ];

  const blocked = rows.filter(r => effectiveSelected.includes(r.id) && r.validation_error);
  const selectedDuplicates = rows.filter(
    r => effectiveSelected.includes(r.id) && r.status === 'DUPLICATE'
  ).length;
  const pending = attempt?.counts?.PENDING || 0;
  const statusMeta = ATTEMPT_STATUS_META[attempt?.status];

  return (
    <Card
      title={
        <Space>
          <Button size="small" icon={<ArrowLeftOutlined />} onClick={onBack}>
            Vsi poskusi
          </Button>
          <span>{attempt?.source_ref || 'Poskus uvoza'}</span>
          {statusMeta && <Tag color={statusMeta.color}>{statusMeta.label}</Tag>}
        </Space>
      }
      extra={
        <Space wrap>
          <Typography.Text type="secondary">Izbranih: {effectiveSelected.length}</Typography.Text>
          <CategorySelect
            size="small" minWidth={180} value={undefined}
            placeholder={effectiveSelected.length
              ? `${label('category')} za izbrane`
              : `${label('category')} za vse`}
            onChange={(v) => v && bulkSet({ category: v })}
          />
          <Popconfirm
            title="Izbriši izbrane vrstice?"
            okText="Izbriši" cancelText="Prekliči"
            onConfirm={deleteSelected}
          >
            <Button size="small" danger icon={<DeleteOutlined />}
              disabled={!effectiveSelected.length || saving}>
              Izbriši
            </Button>
          </Popconfirm>
          <Button
            type="primary" size="small" icon={<CheckOutlined />}
            loading={saving}
            disabled={!effectiveSelected.length || blocked.length > 0}
            onClick={() => save({ force: selectedDuplicates > 0 })}
          >
            Shrani izbrane ({effectiveSelected.length})
          </Button>
          <Popconfirm
            title="Shrani vse pripravljene vrstice?"
            description={`Shranjene bodo vse vrstice v stanju "Za pregled" (${pending}). Ko ne ostane nič za pregled, je poskus zaključen.`}
            okText="Shrani" cancelText="Prekliči"
            onConfirm={() => save({ whole: true })}
          >
            <Button size="small" loading={saving} disabled={!pending}>
              Shrani vse ({pending})
            </Button>
          </Popconfirm>
        </Space>
      }
    >
      {attempt?.status === 'PROMOTED' && (
        <Alert
          type="success" showIcon style={{ marginBottom: 12 }}
          message="Ta poskus je zaključen"
          description="Vse vrstice so shranjene med transakcije."
        />
      )}
      {blocked.length > 0 && (
        <Alert
          type="warning" showIcon style={{ marginBottom: 12 }}
          message={`${blocked.length} izbranih vrstic še ni pripravljenih`}
          description={blocked[0].validation_error}
        />
      )}
      {selectedDuplicates > 0 && (
        <Alert
          type="info" showIcon style={{ marginBottom: 12 }}
          message={`Med izbranimi je ${selectedDuplicates} podvojenih vrstic`}
          description="Te transakcije v sistemu že obstajajo. Če jih shranite, bodo podvojene."
        />
      )}
      {progress && (
        // Shown in place of nothing at all: a long statement spends minutes in
        // here, and a spinner on the button does not say how far along it is.
        <div style={{ marginBottom: 12 }}>
          <Typography.Text type="secondary">
            {`Shranjevanje ... ${progress.done} od ${progress.total} vrstic`}
          </Typography.Text>
          <Progress
            status="active"
            percent={progress.total
              ? Math.round((progress.done / progress.total) * 100)
              : 0}
          />
        </div>
      )}
      {/* Only worth showing once there is more than one kind of row to look
          at -- a uniform batch gets a single button that does nothing. */}
      {filterOptions.length > 1 && (
        <Space style={{ marginBottom: 12 }} wrap>
          <Typography.Text type="secondary">Prikaži:</Typography.Text>
          <Segmented
            size="small"
            value={statusFilter}
            options={filterOptions}
            onChange={(v) => { setStatusFilter(v); setPage(1); }}
          />
          {statusFilter !== ALL && (
            <Typography.Text type="secondary">
              {`${visibleRows.length} od ${rows.length} vrstic`}
            </Typography.Text>
          )}
        </Space>
      )}
      <Table
        size="small"
        loading={loading}
        columns={columns}
        dataSource={visibleRows}
        rowKey="id"
        pagination={{
          // Display only. The whole batch is in memory -- the filter above and
          // every action below work on all of it -- but a year's statement is a
          // thousand rows of live selects, and rendering those in one go locks
          // the tab for the best part of a minute.
          current: page,
          pageSize,
          total: visibleRows.length,
          onChange: (p, size) => { setPage(p); setPageSize(size); },
          showSizeChanger: true,
          pageSizeOptions: [50, 100, 200, 500],
          hideOnSinglePage: true,
          showTotal: (total) => `${total} vrstic`,
          size: 'small',
        }}
        scroll={{ x: 'max-content' }}
        locale={{
          emptyText: statusFilter === ALL
            ? 'Ni vrstic'
            : 'Nobena vrstica ne ustreza temu filtru',
        }}
        rowClassName={(row) => (row.status === 'DUPLICATE' ? 'row-warning' : '')}
        rowSelection={{
          selectedRowKeys: effectiveSelected,
          // Ticking the header box while filtered selects exactly the rows on
          // screen, which is the point of filtering to "Z napako" in the first
          // place. The table only ever reports keys it can see, so whatever was
          // ticked under another filter is carried over by hand -- otherwise a
          // single click here would silently drop it.
          onChange: (keys) => setSelected([
            ...selected.filter(id => !visibleIds.has(id)),
            ...keys,
          ]),
          getCheckboxProps: (row) => ({ disabled: row.status === 'PROMOTED' }),
        }}
        expandable={{
          // The original statement line, so a bad mapping is always visible.
          expandedRowRender: (row) => (
            <Space size={16} wrap>
              {Object.entries(row.raw || {}).map(([k, v]) => (
                <Typography.Text key={k} type="secondary">
                  <strong>{k}:</strong> {String(v ?? '')}
                </Typography.Text>
              ))}
            </Space>
          ),
          rowExpandable: (row) => Object.keys(row.raw || {}).length > 0,
        }}
      />
    </Card>
  );
};

export default AttemptReview;
